import { NavLink } from "react-router-dom";
import { Pin, PinOff, Star } from "lucide-react";
import { labelSlug } from "./utils";

/**
 * A single leaf item inside a group or the pinned section.
 *
 * Handles three visual modes:
 *   1. Disabled — greyed, unclickable, TBD badge
 *   2. Normal   — pin icon on hover; ⭐ badge if item is pinned
 *   3. Collapsed rail — icon-only, tooltip on hover
 */
export default function SidebarItemRow({
  item,
  collapsed,
  isActive,
  isPinnedItem,
  showStar,
  onTogglePin,
  onContextMenu,
  testIdPrefix,
}) {
  const Icon = item.icon;
  const testId = `${testIdPrefix}-${labelSlug(item.label)}`;

  // Disabled = greyed out, not clickable, no pin UI, no active state, no right-click.
  if (item.disabled) {
    return (
      <div
        data-testid={testId}
        data-disabled="true"
        aria-disabled="true"
        title={collapsed ? `${item.label} — coming soon` : undefined}
        className={`group relative flex items-center ${collapsed ? "justify-center px-2" : "pl-8 pr-2"} py-2 rounded-md text-sm text-slate-600 cursor-not-allowed select-none`}
      >
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <Icon className="w-4 h-4 shrink-0 text-slate-600" />
          {!collapsed && <span className="truncate text-sm">{item.label}</span>}
        </div>
        {!collapsed && (
          <span
            data-testid={`${testId}-tbd`}
            className="ml-auto shrink-0 text-[8.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.03] text-slate-500 border border-white/[0.06]"
          >
            TBD
          </span>
        )}
      </div>
    );
  }

  const inner = (
    <div className="flex items-center gap-2.5 min-w-0 flex-1">
      <Icon className={`w-4 h-4 shrink-0 ${item.color || "text-slate-300"}`} />
      {!collapsed && <span className="truncate text-sm">{item.label}</span>}
      {!collapsed && showStar && isPinnedItem && (
        <Star className="w-3 h-3 shrink-0 text-orange-400 fill-orange-400/40 ml-1" data-testid={`${testId}-star`} />
      )}
    </div>
  );

  return (
    <NavLink
      to={item.to}
      end
      onContextMenu={onContextMenu}
      data-testid={testId}
      className={({ isActive: navActive }) => {
        const active = isActive ?? navActive;
        return `group relative flex items-center ${collapsed ? "justify-center px-2" : "pl-8 pr-2"} py-2 rounded-md text-sm transition-colors ${
          active ? "bg-white/[0.08] text-white" : "text-slate-300 hover:bg-white/[0.04] hover:text-white"
        }`;
      }}
    >
      {({ isActive: navActive }) => (
        <>
          {(navActive || isActive) && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-orange-400" aria-hidden="true" />
          )}
          {inner}
          {!collapsed && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTogglePin(); }}
              data-testid={`${testId}-pin`}
              aria-label={isPinnedItem ? "Unpin" : "Pin to sidebar"}
              className={`ml-auto shrink-0 w-6 h-6 -mr-1 grid place-items-center rounded transition-opacity ${
                isPinnedItem
                  ? "opacity-100 text-orange-400 hover:bg-white/5"
                  : "opacity-0 group-hover:opacity-100 text-slate-400 hover:text-orange-400 hover:bg-white/5"
              }`}
            >
              {isPinnedItem ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
            </button>
          )}
        </>
      )}
    </NavLink>
  );
}
